import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus, LogOut, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  adminLogin,
  getSettings,
  updateSettings,
  getOrders,
  getDiscounts,
  createDiscount,
  updateDiscount,
  deleteDiscount,
  updateOrderStatus,
  archiveOrder,
  deleteOrder,
  cleanupPendingOrders,
  exportOrdersCsv,
  adminRecover,
  adminChangePassword,
  getSecurityConfig,
  updateSecurityConfig,
  getEmailConfig,
  updateEmailConfig,
  sendTestEmail,
} from "../lib/api";
import { inputCls, EMPTY, EMPTY_DISCOUNT, STATUS_LABELS } from "../components/admin/adminConstants";
import { CatalogView } from "../components/admin/CatalogView";
import { OrdersView } from "../components/admin/OrdersView";
import { DiscountsView } from "../components/admin/DiscountsView";
import { SettingsView } from "../components/admin/SettingsView";
import { EmailView } from "../components/admin/EmailView";
import { SocialView } from "../components/admin/SocialView";
import { FestivalsView } from "../components/admin/FestivalsView";

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin(password);
      localStorage.setItem("admin_token", res.token);
      if (res.password_expired) {
        toast.warning("Password scaduta: cambiala in Impostazioni → Sicurezza.");
      } else if (res.days_until_expiry <= 7) {
        toast.warning(`La password scade tra ${res.days_until_expiry} giorni.`);
      }
      onLogin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password errata");
    } finally {
      setLoading(false);
    }
  };

  const recover = async () => {
    setRecovering(true);
    try {
      await adminRecover(window.location.origin);
      toast.success("Se l'account esiste, un link di recupero è stato inviato all'email di amministrazione.");
    } catch {
      toast.error("Impossibile inviare il link ora. Riprova più tardi.");
    } finally {
      setRecovering(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D110D] flex items-center justify-center px-6">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
        data-testid="admin-login-form"
      >
        <Link to="/" className="inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-widest text-[#E8E2D2]/40 hover:text-[#D47738] mb-10">
          <ArrowLeft className="w-3 h-3" /> Torna al sito
        </Link>
        <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#D47738] mb-4">
          Area Riservata
        </p>
        <h1 className="font-serif-display text-5xl text-[#E8E2D2] mb-8 leading-none">
          Amministrazione
        </h1>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className={inputCls}
          data-testid="admin-password-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="w-full mt-4 bg-[#D47738] text-[#0D110D] py-4 font-mono-accent text-xs uppercase tracking-[0.2em] hover:bg-[#E8E2D2] transition-colors duration-300 disabled:opacity-50"
          data-testid="admin-login-btn"
        >
          {loading ? "Accesso..." : "Entra"}
        </button>
        <button
          type="button"
          onClick={recover}
          disabled={recovering}
          className="w-full mt-5 font-mono-accent text-[11px] uppercase tracking-widest text-[#E8E2D2]/50 hover:text-[#D47738] transition-colors disabled:opacity-40"
          data-testid="forgot-password-btn"
        >
          {recovering ? "Invio in corso..." : "Password dimenticata?"}
        </button>
        <p className="mt-3 text-center font-mono-accent text-[9px] uppercase tracking-widest text-[#E8E2D2]/25">
          Il link arriva solo all'email di amministrazione
        </p>
      </motion.form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("admin_token"));
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [cartEnabled, setCartEnabled] = useState(true);
  const [view, setView] = useState("catalog");
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [discountForm, setDiscountForm] = useState(EMPTY_DISCOUNT);
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [contact, setContact] = useState({ address: "", email: "", phone: "" });
  const [socials, setSocials] = useState([]);
  const [shipping, setShipping] = useState({ enabled: true, free_over: 0, default_cost: 0, tiers: [] });
  const [promo, setPromo] = useState({ enabled: false, text: "", link_label: "", link_url: "" });
  const [footerInfo, setFooterInfo] = useState({ p_iva: "", footer_text: "" });
  const [minOrderQty, setMinOrderQty] = useState(12);
  const [legal, setLegal] = useState({ legal_purchase: "", legal_conditions: "", legal_delivery: "", legal_refund: "" });
  const [emailCfg, setEmailCfg] = useState({ configured: false, enabled: false, tenant_id: "", client_id: "", sender_email: "", has_secret: false });
  const [emailSecret, setEmailSecret] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailTestTo, setEmailTestTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [security, setSecurity] = useState({ recovery_email_alt: "", max_age_days: 90, days_until_expiry: null, password_expired: false, admin_email: "" });
  const [savingSecurity, setSavingSecurity] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [ageGateEnabled, setAgeGateEnabled] = useState(true);

  const load = useCallback(() => getProducts().then(setProducts).catch(() => {}), []);
  const loadOrders = useCallback(
    (status, archived) => getOrders(status || undefined, archived).then(setOrders).catch(() => {}),
    []
  );
  const loadDiscounts = useCallback(() => getDiscounts().then(setDiscounts).catch(() => {}), []);
  const loadSecurity = useCallback(() => getSecurityConfig().then(setSecurity).catch(() => {}), []);
  const loadEmailConfig = useCallback(() => getEmailConfig().then(setEmailCfg).catch(() => {}), []);
  const loadSettings = useCallback(
    () =>
      getSettings()
        .then((s) => {
          setCartEnabled(s.cart_enabled);
          setMaintenanceMode(!!s.maintenance_mode);
          setAgeGateEnabled(s.age_gate_enabled !== false);
          setContact(s.contact || { address: "", email: "", phone: "" });
          setSocials(s.socials || []);
          setShipping(s.shipping || { enabled: true, free_over: 0, default_cost: 0, tiers: [] });
          setPromo(s.promo_banner || { enabled: false, text: "", link_label: "", link_url: "" });
          setFooterInfo({ p_iva: s.p_iva || "", footer_text: s.footer_text || "" });
          setMinOrderQty(Number(s.min_order_qty) || 12);
          setLegal({
            legal_purchase: s.legal_purchase || "",
            legal_conditions: s.legal_conditions || "",
            legal_delivery: s.legal_delivery || "",
            legal_refund: s.legal_refund || "",
          });
        })
        .catch(() => {}),
    []
  );

  useEffect(() => {
    if (!authed) return;
    load();
    loadOrders("", false);
    loadDiscounts();
    loadSecurity();
    loadSettings();
    loadEmailConfig();
  }, [authed, load, loadOrders, loadDiscounts, loadSecurity, loadSettings, loadEmailConfig]);

  const saveSecurity = async () => {
    setSavingSecurity(true);
    try {
      const res = await updateSecurityConfig({
        recovery_email_alt: security.recovery_email_alt || "",
        max_age_days: parseInt(security.max_age_days) || 90,
      });
      setSecurity(res);
      toast.success("Impostazioni di sicurezza salvate");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingSecurity(false);
    }
  };

  const saveEmailConfig = async () => {
    setSavingEmail(true);
    try {
      const res = await updateEmailConfig({
        tenant_id: emailCfg.tenant_id || "",
        client_id: emailCfg.client_id || "",
        client_secret: emailSecret || "",
        sender_email: emailCfg.sender_email || "",
        enabled: !!emailCfg.enabled,
      });
      setEmailCfg(res);
      setEmailSecret("");
      toast.success("Configurazione email salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingEmail(false);
    }
  };

  const sendEmailTest = async () => {
    setSendingTest(true);
    try {
      const res = await sendTestEmail(emailTestTo || "");
      if (res.sent) {
        toast.success(`Email di prova inviata a ${res.to}`);
      } else {
        toast.error(`Invio non riuscito: ${res.error || "errore sconosciuto"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    } finally {
      setSendingTest(false);
    }
  };


  const changePassword = async () => {
    if (!currentPassword) {
      toast.error("Inserisci la password attuale");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("La password deve avere almeno 8 caratteri");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    setChangingPw(true);
    try {
      await adminChangePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password aggiornata");
      loadSecurity();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel cambio password");
    } finally {
      setChangingPw(false);
    }
  };

  const filterOrders = (status) => {
    setOrderFilter(status);
    loadOrders(status, showArchived);
  };

  const toggleArchivedView = (archived) => {
    setShowArchived(archived);
    setOrderFilter("");
    loadOrders("", archived);
  };

  const archiveOrderAction = async (sessionId, archived) => {
    setOrders((prev) => prev.filter((o) => o.session_id !== sessionId));
    try {
      await archiveOrder(sessionId, archived);
      toast.success(archived ? "Ordine archiviato" : "Ordine ripristinato");
    } catch {
      toast.error("Errore nell'operazione");
      loadOrders(orderFilter, showArchived);
    }
  };

  const deleteOrderAction = async (sessionId) => {
    if (!window.confirm("Eliminare definitivamente questo ordine? L'operazione non è reversibile.")) return;
    setOrders((prev) => prev.filter((o) => o.session_id !== sessionId));
    try {
      await deleteOrder(sessionId);
      toast.success("Ordine eliminato");
    } catch {
      toast.error("Errore nell'eliminazione");
      loadOrders(orderFilter, showArchived);
    }
  };

  const cleanupPendingAction = async () => {
    if (!window.confirm("Eliminare tutti gli ordini di prova non pagati (PENDING)? L'operazione non è reversibile.")) return;
    try {
      const res = await cleanupPendingOrders();
      toast.success(`${res.deleted || 0} ordini di prova eliminati`);
      loadOrders(orderFilter, showArchived);
    } catch {
      toast.error("Errore nella pulizia");
    }
  };

  const changeOrderStatus = async (sessionId, status) => {
    setOrders((prev) => prev.map((o) => (o.session_id === sessionId ? { ...o, order_status: status } : o)));
    try {
      await updateOrderStatus(sessionId, status);
      toast.success(`Stato aggiornato: ${STATUS_LABELS[status]}`);
      if (orderFilter) loadOrders(orderFilter, showArchived);
    } catch {
      toast.error("Errore nel cambio stato");
      loadOrders(orderFilter, showArchived);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const blob = await exportOrdersCsv();
      const url = window.URL.createObjectURL(new Blob([blob], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ordini_vecchiocinghiale_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV avviato");
    } catch {
      toast.error("Errore nell'export");
    } finally {
      setExporting(false);
    }
  };

  const openNewDiscount = () => {
    setEditingDiscount(null);
    setDiscountForm(EMPTY_DISCOUNT);
    setDiscountDialogOpen(true);
  };

  const openEditDiscount = (d) => {
    setEditingDiscount(d);
    setDiscountForm({
      code: d.code,
      type: d.type || "percent",
      value: d.value ?? 0,
      active: d.active !== false,
      min_subtotal: d.min_subtotal ?? 0,
      expires_at: d.expires_at || "",
      max_uses: d.max_uses ?? 0,
    });
    setDiscountDialogOpen(true);
  };

  const saveDiscount = async () => {
    if (!discountForm.code.trim()) {
      toast.error("Inserisci un codice");
      return;
    }
    setSavingDiscount(true);
    const payload = {
      code: discountForm.code.trim().toUpperCase(),
      type: discountForm.type,
      value: parseFloat(discountForm.value) || 0,
      active: !!discountForm.active,
      min_subtotal: parseFloat(discountForm.min_subtotal) || 0,
      expires_at: discountForm.expires_at || null,
      max_uses: parseInt(discountForm.max_uses) || 0,
    };
    try {
      if (editingDiscount) {
        await updateDiscount(editingDiscount.id, payload);
        toast.success("Codice aggiornato");
      } else {
        await createDiscount(payload);
        toast.success("Codice creato");
      }
      setDiscountDialogOpen(false);
      loadDiscounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingDiscount(false);
    }
  };

  const removeDiscount = async (d) => {
    if (!window.confirm(`Eliminare il codice "${d.code}"?`)) return;
    try {
      await deleteDiscount(d.id);
      toast.success("Codice eliminato");
      loadDiscounts();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const toggleMaintenance = async (value) => {
    setMaintenanceMode(value);
    try {
      await updateSettings({ maintenance_mode: value });
      toast.success(value ? "Sito in manutenzione ATTIVO" : "Sito online");
    } catch {
      setMaintenanceMode(!value);
      toast.error("Errore nel salvataggio impostazione");
    }
  };

  const toggleAgeGate = async (value) => {
    setAgeGateEnabled(value);
    try {
      await updateSettings({ age_gate_enabled: value });
      toast.success(value ? "Verifica età (18+) ATTIVA" : "Verifica età DISATTIVATA");
    } catch {
      setAgeGateEnabled(!value);
      toast.error("Errore nel salvataggio impostazione");
    }
  };

  const saveFooterSettings = async () => {
    setSavingSettings(true);
    try {
      const cleanShipping = {
        enabled: !!shipping.enabled,
        free_over: parseFloat(shipping.free_over) || 0,
        default_cost: parseFloat(shipping.default_cost) || 0,
        tiers: (shipping.tiers || [])
          .map((t) => ({ up_to: parseFloat(t.up_to) || 0, cost: parseFloat(t.cost) || 0 }))
          .filter((t) => t.up_to > 0),
      };
      await updateSettings({ contact, socials: socials.filter((s) => s.label.trim()), shipping: cleanShipping, promo_banner: {
        enabled: !!promo.enabled,
        text: (promo.text || "").trim(),
        link_label: (promo.link_label || "").trim(),
        link_url: (promo.link_url || "").trim(),
      }, p_iva: footerInfo.p_iva, footer_text: footerInfo.footer_text,
        min_order_qty: parseInt(minOrderQty) || 12,
        legal_purchase: legal.legal_purchase, legal_conditions: legal.legal_conditions,
        legal_delivery: legal.legal_delivery, legal_refund: legal.legal_refund });
      toast.success("Impostazioni salvate");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleCart = async (value) => {
    setCartEnabled(value);
    try {
      await updateSettings({ cart_enabled: value });
      toast.success(value ? "Carrello abilitato" : "Carrello disabilitato");
    } catch {
      setCartEnabled(!value);
      toast.error("Errore nel salvataggio impostazione");
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, compare_at_price: p.compare_at_price ?? "", food_pairings: (p.food_pairings || []).join(", "), images: (p.images || []).join(", ") });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      abv: parseFloat(form.abv),
      ibu: form.ibu ? parseInt(form.ibu) : null,
      price: parseFloat(form.price),
      compare_at_price: form.compare_at_price !== "" && form.compare_at_price != null ? parseFloat(form.compare_at_price) : null,
      stock: parseInt(form.stock),
      food_pairings: String(form.food_pairings || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      images: String(form.images || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      recipe_story: form.recipe_story || "",
    };
    try {
      if (editing) {
        await updateProduct(editing.id, payload);
        toast.success("Prodotto aggiornato");
      } else {
        await createProduct(payload);
        toast.success("Prodotto creato");
      }
      setDialogOpen(false);
      load();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"?`)) return;
    try {
      await deleteProduct(p.id);
      toast.success("Prodotto eliminato");
      load();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setAuthed(false);
  };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  const ctx = {
    // catalog
    cartEnabled, toggleCart, products, openEdit, remove,
    dialogOpen, setDialogOpen, editing, form, setForm, save, saving,
    // orders
    showArchived, toggleArchivedView, orderFilter, filterOrders, orders,
    cleanupPendingAction, exportCsv, exporting, changeOrderStatus,
    archiveOrderAction, deleteOrderAction,
    // discounts
    discounts, openEditDiscount, removeDiscount,
    discountDialogOpen, setDiscountDialogOpen, discountForm, setDiscountForm,
    saveDiscount, savingDiscount, editingDiscount,
    // settings
    maintenanceMode, toggleMaintenance, ageGateEnabled, toggleAgeGate, contact, setContact, socials, setSocials,
    promo, setPromo, shipping, setShipping, saveFooterSettings, savingSettings,
    footerInfo, setFooterInfo, legal, setLegal,
    minOrderQty, setMinOrderQty,
    security, setSecurity, saveSecurity, savingSecurity,
    newPassword, setNewPassword, confirmPassword, setConfirmPassword, changePassword, changingPw,
    currentPassword, setCurrentPassword,
    // email
    emailCfg, setEmailCfg, emailSecret, setEmailSecret,
    saveEmailConfig, savingEmail, emailTestTo, setEmailTestTo, sendEmailTest, sendingTest,
  };

  return (
    <div className="min-h-screen bg-[#0D110D] text-[#E8E2D2]" data-testid="admin-panel">
      <header className="border-b border-white/10 px-6 lg:px-12 py-6 flex items-center justify-between">
        <div>
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#D47738]">
            Vecchio Cinghiale — Gestione
          </p>
          <h1 className="font-serif-display text-3xl" data-testid="admin-page-title">{
            {catalog: "Catalogo Birre", orders: "Ordini", discounts: "Sconti", settings: "Impostazioni", email: "Email — Microsoft 365", social: "Kit Social", festivals: "Feste ed Eventi"}[view] || "Gestione"
          }</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex border border-white/20" data-testid="admin-tabs">
            <button
              onClick={() => setView("catalog")}
              className={`px-5 py-3 font-mono-accent text-[11px] uppercase tracking-[0.15em] transition-colors ${view === "catalog" ? "bg-[#D47738] text-[#0D110D]" : "text-[#E8E2D2]/70 hover:text-[#E8E2D2]"}`}
              data-testid="tab-catalog"
            >
              Catalogo
            </button>
            <button
              onClick={() => { setView("orders"); loadOrders(orderFilter, showArchived); }}
              className={`px-5 py-3 font-mono-accent text-[11px] uppercase tracking-[0.15em] transition-colors ${view === "orders" ? "bg-[#D47738] text-[#0D110D]" : "text-[#E8E2D2]/70 hover:text-[#E8E2D2]"}`}
              data-testid="tab-orders"
            >
              Ordini ({orders.length})
            </button>
            <button
              onClick={() => { setView("discounts"); loadDiscounts(); }}
              className={`px-5 py-3 font-mono-accent text-[11px] uppercase tracking-[0.15em] transition-colors ${view === "discounts" ? "bg-[#D47738] text-[#0D110D]" : "text-[#E8E2D2]/70 hover:text-[#E8E2D2]"}`}
              data-testid="tab-discounts"
            >
              Sconti
            </button>
            <button
              onClick={() => setView("settings")}
              className={`px-5 py-3 font-mono-accent text-[11px] uppercase tracking-[0.15em] transition-colors ${view === "settings" ? "bg-[#D47738] text-[#0D110D]" : "text-[#E8E2D2]/70 hover:text-[#E8E2D2]"}`}
              data-testid="tab-settings"
            >
              Impostazioni
            </button>
            <button
              onClick={() => { setView("email"); loadEmailConfig(); }}
              className={`px-5 py-3 font-mono-accent text-[11px] uppercase tracking-[0.15em] transition-colors ${view === "email" ? "bg-[#D47738] text-[#0D110D]" : "text-[#E8E2D2]/70 hover:text-[#E8E2D2]"}`}
              data-testid="tab-email"
            >
              Email
            </button>
            <button
              onClick={() => setView("social")}
              className={`px-5 py-3 font-mono-accent text-[11px] uppercase tracking-[0.15em] transition-colors ${view === "social" ? "bg-[#D47738] text-[#0D110D]" : "text-[#E8E2D2]/70 hover:text-[#E8E2D2]"}`}
              data-testid="tab-social"
            >
              Social
            </button>
            <button
              onClick={() => setView("festivals")}
              className={`px-5 py-3 font-mono-accent text-[11px] uppercase tracking-[0.15em] transition-colors ${view === "festivals" ? "bg-[#D47738] text-[#0D110D]" : "text-[#E8E2D2]/70 hover:text-[#E8E2D2]"}`}
              data-testid="tab-festivals"
            >
              Feste
            </button>
          </div>
          {view === "catalog" && (
            <button
              onClick={openNew}
              className="flex items-center gap-2 bg-[#D47738] text-[#0D110D] px-6 py-3 font-mono-accent text-xs uppercase tracking-[0.2em] hover:bg-[#E8E2D2] transition-colors"
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4" /> Nuova
            </button>
          )}
          {view === "discounts" && (
            <button
              onClick={openNewDiscount}
              className="flex items-center gap-2 bg-[#D47738] text-[#0D110D] px-6 py-3 font-mono-accent text-xs uppercase tracking-[0.2em] hover:bg-[#E8E2D2] transition-colors"
              data-testid="add-discount-btn"
            >
              <Plus className="w-4 h-4" /> Nuovo codice
            </button>
          )}
          <button
            onClick={logout}
            className="p-3 border border-white/20 hover:border-[#9E3C3C] hover:text-[#9E3C3C] transition-colors"
            data-testid="logout-btn"
            title="Esci"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {view === "settings" ? (
        <SettingsView ctx={ctx} />
      ) : view === "email" ? (
        <EmailView ctx={ctx} />
      ) : view === "social" ? (
        <SocialView />
      ) : view === "festivals" ? (
        <FestivalsView />
      ) : view === "orders" ? (
        <OrdersView ctx={ctx} />
      ) : view === "discounts" ? (
        <DiscountsView ctx={ctx} />
      ) : (
        <CatalogView ctx={ctx} />
      )}
    </div>
  );
}
